"use client";

import { useQueryClient } from "@tanstack/react-query";
import { CalendarHeart } from "lucide-react";
import { useTranslations } from "next-intl";
import { useEffect, useState } from "react";

import { SettingsSection } from "@/components/settings/SettingsSection";
import { Button } from "@/components/ui/button";

/** The weekly meal plan draft switch (on unless turned off); the draft itself lives on the dashboard. */
export function MealPlanSection() {
  const t = useTranslations("settings");
  const queryClient = useQueryClient();
  const [enabled, setEnabled] = useState<boolean | null>(null);

  useEffect(() => {
    fetch("/api/preferences")
      .then((res) => (res.ok ? res.json() : null))
      .then((prefs: { weeklyMealDraft?: boolean } | null) =>
        setEnabled(prefs?.weeklyMealDraft ?? true)
      )
      .catch(() => setEnabled(true));
  }, []);

  const toggle = async () => {
    if (enabled === null) return;
    const next = !enabled;
    setEnabled(next);
    const res = await fetch("/api/preferences", {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ weeklyMealDraft: next }),
    }).catch(() => null);
    if (!res?.ok) {
      setEnabled(!next);
      return;
    }
    queryClient.removeQueries({ queryKey: ["mealPlanDraft"] });
  };

  return (
    <SettingsSection>
      <div className="flex items-center justify-between gap-4">
        <div className="flex min-w-0 items-center gap-4">
          <CalendarHeart className="size-5 shrink-0 text-muted-foreground" aria-hidden />
          <div className="min-w-0">
            <p className="font-medium">{t("weeklyMealDraft")}</p>
            <p className="text-sm text-muted-foreground">{t("weeklyMealDraftDescription")}</p>
          </div>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={toggle}
          disabled={enabled === null}
          aria-pressed={enabled ?? undefined}
          className="shrink-0"
        >
          {enabled === false ? t("off") : t("on")}
        </Button>
      </div>
    </SettingsSection>
  );
}
